import { makeStyles } from '@material-ui/styles';
import { PartnerAd, PartnerAdPlacement, PartnerAdVariant } from '@skyslope/partnerships-sdk';
import {
  PartnerAdInternal,
  PartnershipAdCloseButton,
  PartnershipsErrorBoundary,
  useAd,
  PartnershipsFileMetadata,
} from '@skyslope/partnerships-sdk/react';
import { colors } from '@material-ui/core';
import { useSessionStorageAdContext } from './DigiSignAdsProvider';
import React, { useEffect } from 'react';
import { IRootState } from '../store';
import { useSelector } from 'react-redux';
import { tokenInfo } from '../lib/token';

const useStyles = makeStyles({
  dsAdBox: {
    position: 'relative',
  },
  dsAdCloseButton: {
    position: 'absolute',
    top: 8,
    right: 8,
    backgroundColor: 'white',
    cursor: 'pointer',
    border: 0,
    borderRadius: '500px',
    padding: '6px',
    lineHeight: 1,
    color: 'rgba(0, 0, 0, 0.54)',
    fontSize: '1.125rem',
  },
  closeIcon: {
    fontSize: '20px',
    color: colors.grey[800],
  },
});

export interface IDSAdProps {
  adVariant: PartnerAdVariant;
  id: string;
  imgProps?: React.ImgHTMLAttributes<HTMLImageElement>;
}

const useEnvelopeData = () => {
  return useSelector((state: IRootState) => state.envelope.envelope);
};

const DSAd: React.FC<IDSAdProps> = (props: IDSAdProps) => {
  const classes = useStyles();

  const envelope = useEnvelopeData();
  const formTypes = envelope?.documents
    .filter(document => document.metadata?.formType != null)
    .map(document => document.metadata.formType);
  const filterFn = (partnerAd: PartnerAd): boolean => {
    let showOnlyForFormTypes = partnerAd.metadata?.showOnlyForFormTypes;
    if (typeof showOnlyForFormTypes === 'string') {
      showOnlyForFormTypes = [showOnlyForFormTypes];
    }
    return (showOnlyForFormTypes as string[])?.some(formType => formTypes?.includes(formType)) ?? false;
  };

  const { ad } = useAd({ variant: props.adVariant, placement: PartnerAdPlacement.General, filterFn });

  const {
    adDismissedCollection,
    setAdDismissedCollection,
    adVisibleCollection,
    setAdVisibleCollection,
  } = useSessionStorageAdContext();
  const adDismissedCollectionParsed = JSON.parse(adDismissedCollection || '{}');
  const handleCloseButtonClick = () => {
    adDismissedCollectionParsed[props.id] = 'false';
    setAdDismissedCollection(JSON.stringify(adDismissedCollectionParsed));
  };
  const userMetadata: PartnershipsFileMetadata = {
    clientDetails: [
      {
        name: `${envelope?.signer?.firstName} ${envelope?.signer?.middleName} ${envelope?.signer?.lastName}`,
        email: `${tokenInfo?.Email ?? ''}`,
      },
    ],
    subscriberId: `${envelope?.metadata?.ownerSubscriberId}`,
  };

  useEffect(() => {
    if (ad == null || adDismissedCollectionParsed[props.id] === 'false') {
      setAdVisibleCollection(prevState => ({
        ...prevState,
        [props.id]: false,
      }));
    } else {
      setAdVisibleCollection(prevState => ({
        ...prevState,
        [props.id]: true,
      }));
    }
  }, [ad, adDismissedCollection]);

  if (!adVisibleCollection[props.id]) {
    return null;
  }

  return (
    <PartnershipsErrorBoundary>
      <div className={classes.dsAdBox}>
        <PartnerAdInternal
          ad={ad}
          id={props.id}
          variant={props.adVariant}
          imgProps={props.imgProps}
          metadata={userMetadata}
        />
        <PartnershipAdCloseButton
          onDismissed={handleCloseButtonClick}
          buttonProps={{ className: classes.dsAdCloseButton }}
        ></PartnershipAdCloseButton>
      </div>
    </PartnershipsErrorBoundary>
  );
};

export default DSAd;
